import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon } from '@BICE_Vida/components';

import classNames from 'classnames';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import HeaderCustom from '@/components/HeaderCustom';
import PromoHeader from '@/components/PromoHeader';
import { AvailablePlans as AvailablePlansType } from '../../types/availablePlans';

import { appTitle, breakPointMd } from '@/utils/constants';
import { headerSections, headerSectionTexts, landingText } from '@/utils/generalTexts';
import routes from '@/utils/routes';
import { useCyberDates, useBranding } from '@/src/customHooks';
import * as plansSelectors from '@/redux/plans/selectors';

import * as wizardActions from '@/redux/wizard/actions';
import * as wizardSelectors from '@/redux/wizard/selectors';

import styles from './LayoutCustom.module.scss';
import { trackClick, clickActionsEvents } from '@/utils/dataLayer/home';
import { EventParamsT } from '@/utils/dataLayer/baseDataLayer';
import PreexistingConditionsHeader from '../PreexistingConditionsHeader';
import componentsId from '@/utils/componentsId';
import Sidebar from '../Home/Hero/Sidebar';
import Toaster from '@/designSystem/Toaster';

type LayoutT = {
  children?: JSX.Element | JSX.Element[];
  title?: string;
  shouldRenderCyberDayHeader?: boolean;
  eventParams?: EventParamsT;
};

const NEW_ORIGIN = 'https://www.bicevida.cl';

function LayoutCustom({
  children,
  title = appTitle,
  shouldRenderCyberDayHeader = false,
  eventParams,
}: LayoutT) {
  const router = useRouter();
  useBranding();
  const { promo } = useSelector(wizardSelectors.getBranding);
  const promoCyberData = useCyberDates(new Date());
  const { isCyberDayToday } = promoCyberData;
  const [isMobile, setIsMobile] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showToaster, setShowToaster] = useState(false);

  const handleSidebar = () => setShowSidebar(!showSidebar);

  const branding = useSelector(wizardSelectors.getBranding);

  const hiringBoxRef = useRef<HTMLDivElement>(null);
  const [identifier, setIdentifier] = useState('');

  const dispatch = useDispatch();
  const rehydrated = useSelector(({ _persist }: any) => _persist?.rehydrated);

  const availablePlans: AvailablePlansType = useSelector(plansSelectors.getAvailablePlans);

  const showPreexistingConditionsBanner = false;

  const plansIncludePreexistingConditions = useMemo(
    () => availablePlans.filter((card) => card.base !== null && card.hidePlan !== true),
    [availablePlans]
  );

  useEffect(() => {
    setIsMobile(window.innerWidth < breakPointMd);
  }, []);

  const onScroll = useCallback(() => {
    const windowHeight = window.innerHeight;
    const hiringBox = hiringBoxRef?.current;
    const scroll = window.pageYOffset;

    if (hiringBox && hiringBox.getBoundingClientRect().y <= windowHeight - 50) {
      const sectionTop: number = -scroll || 0;
      const offset: number = -scroll / 2;
      const opacity = 1 - (sectionTop - offset + 300) / 100;

      hiringBox.style.setProperty('--opacity', `${opacity - 1}`);
      hiringBox.style.setProperty('--position-y', `-${sectionTop}px`);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (rehydrated) {
      dispatch(wizardActions.resetWizard());
    }
  }, [rehydrated]);

  const btnRecoverReHiringClick = () => {
    trackClick({ action: `Click Retoma tu contratación - Menú` });

    router.push(routes.recoverHiring);
  };

  useEffect(() => {
    const id =
      Object.keys(routes).find((key: string) => (routes[key] === router.pathname ? key : '')) || '';
    setIdentifier(id);
  }, []);

  const btnEstimateClick = () => {
    trackClick({ action: clickActionsEvents.btnHireNow });
    router.push(routes.wizardPlan);
  };

  const layoutClass = classNames(styles.layout, {
    [styles.layout_custom]: isCyberDayToday,
    [styles.layout_custom_preesxistences]: showPreexistingConditionsBanner,
  });

  const navBarClass = classNames(styles.navbar, {
    [styles.preexisting_navbar]: showPreexistingConditionsBanner && isMobile,
  });

  useEffect(() => {
    if (router.isReady) {
      const oldOrigin = process.env.baseUrl || 'http://localhost:4000';
      const stage = process.env.stage || '';

      if (window.location.origin === oldOrigin) {
        const newUrl =
          NEW_ORIGIN + window.location.pathname + window.location.search + window.location.hash;

        if (stage === 'prod') window.location.replace(newUrl);
        // eslint-disable-next-line no-console
        else console.log('Redirection to:', newUrl);
      }
    }
  }, [router.isReady]);

  return (
    <Fragment>
      <Head>
        <title>{title}</title>
      </Head>
      <HeaderCustom
        brandingLogo={branding?.logo}
        brandingCode={branding?.code}
        sticky
        logoRedirect
        children={
          <Fragment>
            {showPreexistingConditionsBanner && isMobile && (
              <div className={styles.preexisting_conditions_header_mobile_container}>
                <PreexistingConditionsHeader
                  showImage={!isMobile}
                  availablePlans={plansIncludePreexistingConditions.length}
                  className={styles.preexisting_conditions_header_mobile}
                />
              </div>
            )}
            <div className={styles.header_navbar_container}>
              <div className={styles.header_container}>
                <div className={navBarClass}>
                  <Navbar sections={headerSections} active={identifier} />
                </div>
                <Button
                  className={styles.btn_recover}
                  id={`${identifier}_header_btn_recover_hiring`}
                  text={headerSectionTexts.btnRecoverHiring}
                  size="medium"
                  variant="outline"
                  onClick={btnRecoverReHiringClick}
                />
              </div>
              <Button
                className={styles.btn_navbar}
                id={componentsId.landingHeroBtnPrices}
                text={landingText.hero.button}
                size="large"
                onClick={btnEstimateClick}
                aria-label={landingText.hero.button}
              />
              <div className={styles.btn_menu} onClick={handleSidebar}>
                <Icon variant="default" name={showSidebar ? 'close' : 'menu'} size="medium" />
              </div>
              <Sidebar show={showSidebar} />
            </div>
          </Fragment>
        }
      />

      <Toaster
        setHideToaster={() => {
          setShowToaster(false);
        }}
        text="Correo copiado"
        show={showToaster}
        customClass={styles.toast}
        icon="check_circle"
        iconSuccess
      />

      {showPreexistingConditionsBanner && !isMobile && (
        <PreexistingConditionsHeader
          showImage
          availablePlans={plansIncludePreexistingConditions.length}
          className={styles.preexisting_conditions_header}
        />
      )}
      {isCyberDayToday && (
        <div className={styles.sticky}>
          <PromoHeader {...promoCyberData} />
        </div>
      )}
      <div className={layoutClass}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { setShowToaster } as any);
          }
          return child;
        })}
      </div>
      <Footer />
    </Fragment>
  );
}

export default LayoutCustom;
